/**
 * Gearling.ts
 * Rail-walker enemy for World 3 (Brasscloud Foundry).
 *
 * Mechanical ground patroller. Reverses direction on any wall hit OR when
 * the floor ahead drops away — the "rail-walker" never falls off its track.
 * No shield, no projectile, no AI reaction to Nova. Stomp- and shard-killable.
 *
 * State machine: PATROL → HURT → DEAD
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 3 (Brasscloud Foundry) stub. Wired into levels when W3 ships.
 * See ROADMAP.md Phase 4 (`Gearling.ts`).
 *
 * @module Gearling
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime } from '../../engine/gameState';
import { resolveAllPlatforms, hasFloorAhead } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type GearlingState = 'PATROL' | 'HURT' | 'DEAD';

/** Width/height of the Gearling hitbox in logical pixels. */
export const GEARLING_WIDTH = 24;
export const GEARLING_HEIGHT = 22;

/** Walk speed in logical pixels per second — steady mechanical pace. */
const PATROL_SPEED = 60;

/** Duration of the hurt-flash before death in seconds. */
const HURT_DURATION = 0.25;

/** Render colour for greybox — brass plate. */
export const GEARLING_COLOR = '#a8884c';

// DECISION: Reusable peek + hitbox to avoid per-tick allocations in the
// engine hot path (mirrors Shellback / SparkImp).
const _hitbox = { x: 0, y: 0, width: GEARLING_WIDTH, height: GEARLING_HEIGHT };

/**
 * Projectile-impact result. Mirrors the SparkImp union so combat callers
 * have the same shape across shieldless ranged + melee enemies.
 *
 * DECISION: No `'KILLED'` value — `applyShardHit()` never sets `dead = true`
 * synchronously. The HURT → DEAD transition completes inside `update()`
 * once the hurt timer elapses. `'BLOCKED'` is reserved for future shielded
 * variants and never returned today.
 */
export type GearlingHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/**
 * Gearling — rail-walker enemy for World 3 (Brasscloud Foundry).
 *
 * Spawn with `new Gearling(x, y)`. Call `update(dt, level)` each fixed tick.
 * Read `dead` to know when to remove from the scene.
 */
export class Gearling extends BaseEntity {
  public readonly entityType = 'Gearling';
  public readonly color = GEARLING_COLOR;
  public state: GearlingState = 'PATROL';

  /**
   * True once a hit has started the death sequence (`HURT`) or it has
   * finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'HURT' || this.state === 'DEAD';
  }
  /** True when facing right (used by renderer for sprite flip). */
  public facingRight = true;
  private _hurtTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, GEARLING_WIDTH, GEARLING_HEIGHT);
    this.vx = PATROL_SPEED; // start walking right
  }

  /**
   * Advance the gearling by one fixed tick.
   * @param dt    Fixed-step delta in seconds.
   * @param level Current level geometry — read-only.
   */
  public update(dt: number, level: LevelRuntime): void {
    if (this.state === 'DEAD') return;

    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    if (this.state === 'HURT') {
      this._hurtTimer -= dt;
      this._integrateAndResolve(dt, level);
      if (this._hurtTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('Gearling', 'Death after hurt');
      }
      return;
    }

    // PATROL — drive forward, then let the resolver and edge check flip
    // direction if the rail runs out (wall or open air ahead).
    this._integrateAndResolve(dt, level);
    this._handleGroundEdges(level);
  }

  /**
   * Integrate position + velocity, resolve platform overlaps, and flip
   * direction if a wall is hit. Hurt state shares this path so the resolver
   * still zeroes `vy` on landing during the death animation.
   */
  private _integrateAndResolve(dt: number, level: LevelRuntime): void {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;
    if ((r.hitLeft || r.hitRight) && this.state === 'PATROL') {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('Gearling', 'Turn at wall');
    }
  }

  /**
   * Peek one step ahead at floor level. If there is no platform under the
   * imminent next step, reverse direction — Gearlings are mechanical and
   * never walk off the rail.
   */
  private _handleGroundEdges(level: LevelRuntime): void {
    if (this.vy !== 0) return; // Only act when grounded (resolver zeroed vy).
    const hasFloor = hasFloorAhead(
      this.x,
      this.y,
      this.width,
      this.height,
      this.vx,
      level.platforms,
    );
    if (!hasFloor && this.vx !== 0) {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('Gearling', 'Turn at edge');
    }
  }

  /**
   * Apply one projectile hit. No shield — any shard connects.
   * @returns `'HURT'` on a hit that started the death sequence, `'IGNORED'`
   *          if already dead/hurt. Never returns `'BLOCKED'` today.
   */
  public applyShardHit(): GearlingHitResult {
    if (this.state === 'DEAD' || this.state === 'HURT') return 'IGNORED';
    this._enterHurt();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. No protection from above.
   * @returns `true` if the stomp started the death sequence; `false` if already dead/hurt.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    this._enterHurt();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts) — delegates to
   * the specialised methods. Kept thin so `combatStep` can drive Gearling
   * without an external adapter once it is wired into `gameState.enemies`.
   * @param fromStomp `true` for stomp contact, `false`/omitted for shard hits.
   * @returns `true` if this hit started the death sequence; `false` otherwise.
   */
  public applyHit(fromStomp = false): boolean {
    return fromStomp ? this.applyStomp() : this.applyShardHit() === 'HURT';
  }

  private _enterHurt(): void {
    this.state = 'HURT';
    this._hurtTimer = HURT_DURATION;
    this.vx = 0;
    Logger.debug('Gearling', 'Hit — entering HURT');
  }
}
